<!-- Responsible for viewing/editing the currently-loaded game -->

<script setup>
import { computed, reactive, onMounted, ref, watch, nextTick } from 'vue';

import moveAndDelete from '@/components/MoveAndDelete.vue';
import segmentEditor from '@/components/SegmentEditor.vue';

import { useTriviaStore, useWebSocketStore } from '@/stores/trivia.js';
const store = useTriviaStore();
const webSocketStore = useWebSocketStore();



const emit = defineEmits(['close', 'update', 'manager']);


/*
tools => display scores, answers, or question editor
generators => general reference for app use

questions is an array
each item in the array is an object (and represents one round of gameplay)
each 'round' object is number-keyed, and each key is a question
each question is an object that looks like this
{ cat: string     category of question to display
  timeAllotted: number
  q: array, containing question segments
  a: object with text, highlight (number tells the app which multiple choice answer to highlight)
  hint object (for special question types, like wordLeap)
}

The question's q array is filled with object segments,
where the type of question segment (ie text/image) is set,
as well as all the info needed to display that segment. 
*/

const getInitialTools = () => ({
  scores: false,
  answers: false,
  questions: false
});

const getInitialGenerators = () => ({
  wordLeap: false,
  sizeMatters: false,
  boxOffice: false,
  bank:[],
  seed: null,
  highlightedWords: [],
  promptInput: null,
  manager: false,
});

const activeRound = computed ({
  get() {
    return store.CurrentGame.activeRound;
  },
  set(newVal) {
    store.CurrentGame.activeRound = newVal;
  }
});

const activeQuestion = computed ({
  get() {
    return store.CurrentGame.activeQuestion;
  },
  set(newVal) {
    store.CurrentGame.activeQuestion = newVal;
  }
});




const tools = ref(getInitialTools());
const generators = ref(getInitialGenerators());

const hideAnswersInManager = ref(true);



let round = ref(0);
let question = ref(0);



const workingQuestion = computed(() => {
  if (activeRound.value > -1 && activeQuestion.value > -1) {
    try {
      return store.CurrentGame.rounds[activeRound.value][activeQuestion.value];
    } catch {
      activeRound.value = 0;
      activeQuestion.value = 0;
      // round.value = 0;
      // question.value = 0;
      try {
        return store.CurrentGame.rounds[activeRound.value][activeQuestion.value];
      } catch {
        addRound();
        return store.CurrentGame.rounds[activeRound.value][activeQuestion.value];
      }
    }
  }
  return null;
});





const questionOverride = ref(null);

function saveQuestions() {
  store.activeGame = store.CurrentGame;
  emit('manager')
}


function saveGame() {
  if (!store.CurrentGame._id) {
    store.saveGame();
  } else {
    store.updateGame();
  }
  emit('manager')
}

function moveRound(key, up) {
  const keys = Object.keys(store.CurrentGame.rounds);
  const index = keys.indexOf(key);
  const move = up ? index - 1 : index + 1;

  if (move >= 0 && move < keys.length) {
    let temp = store.CurrentGame.rounds[keys[index]];
    store.CurrentGame.rounds[keys[index]] = store.CurrentGame.rounds[keys[move]];
    store.CurrentGame.rounds[keys[move]] = temp;
    activeRound.value = keys[move];
  }
}



async function generateWords(word) {
  let prompt;
  const gen = generators.value;
  if (!gen.promptInput) {
    if (word) {
      generators.value.highlightedWords.push(word);
    }
    if (gen.seed) {
      generators.value.highlightedWords.push(gen.seed);
    }
    if (gen.seed || word) {
      prompt = `Please generate 10 common words that are likely to follow ${word || gen.seed}`;
      if (!word && gen.seed) {
        generators.value.bank.push([gen.seed]);
        generators.value.seed = null;
      }
    } else {
      prompt = 'Please generate 10 common words between 3 and 9 characters.';
    }
  } else {
    prompt = gen.promptInput;
  }
  console.log(prompt);
  // model: 'text-davinci-003',
  const response = await openai.createCompletion({
    model: 'text-davinci-003',
    prompt,
    temperature: 0.6,
    max_tokens: 2500,
  });
  console.log(response.data.choices[0].text);
  const words = removeFormatting(response.data.choices[0].text).split(' ').filter(b => Boolean(b));
  console.log(words);
  generators.value.bank.push(words);
}

async function generateSizeMatters() {
  let prompt = 'Please list 5 animals who normally weigh between 1,000 and 2,000 pounds. Please list the weight next to the animal';
  const response = await openai.createCompletion({
    model: 'text-davinci-003',
    prompt,
    temperature: 0,
    max_tokens: 2000,
  });
  console.log(response);
  generators.value.bank.push(removeFormatting(response.data.choices[0].text).split(' ').filter(b => Boolean(b)));
  // let prompt;
  // if (!generators.value.promptInput) {
  //   if (word) {
  //     generators.value.highlightedWords.push(word);
  //   }
  //   if (generators.value.seed) {
  //     highlightedWords.value.push(seed.value);
  //   }
  //   if (generators.value.seed || word) {
  //     prompt = `Please generate 5 very common words that follow ${word || seed.value}`;
  //     if (!word && seed.value) {
  //       generators.value.bank.push([seed.value]);
  //       generators.value.seed = null;
  //     }
  //   } else {
  //     prompt = 'Please generate 5 very common words between 3 and 9 characters.';
  //   }
  // } else {
  //   prompt = generators.value.promptInput;
  // }
  // console.log(prompt);
  // // model: 'text-davinci-003',
  // const response = await openai.createCompletion({
  //   model: 'text-davinci-003',
  //   prompt,
  //   temperature: 1,
  //   max_tokens: 100,
  // });
  // console.log(response.data.choices[0].text);
  // const words = removeFormatting(response.data.choices[0].text).split(' ').filter(b => Boolean(b));
  // console.log(words);
  // bank.value.push(words);
}


function rerollSeed() {
  generators.value.seed = [];
  generateWords();
}

function removeFormatting(word) {
  // Remove leading numbers from openai api returns
  const array = [];
  word.split('\n').filter(i => Boolean(i)).forEach((wrd) => {
    let toReturn = wrd.split('.');
    toReturn = toReturn.slice(1);
    array.push(toReturn);
  });
  console.log(array.join(' '));
  return array.join(' ');
}


function resetTools() {
  tools.value = getInitialTools();
}
function resetGenerators() {
  generators.value = getInitialGenerators();
}

const newQuestionModel = computed(() => {
  const modelData = {};
  modelData.dateCreated = new Date();
  modelData.dateUpdated = new Date();
  modelData.author = store.user._id;
  modelData.timeAllotted = 60;
  modelData.segments = [];
  modelData.type = '';
  modelData.category = '';
  modelData.categorySubText = '';
  modelData.points = 2;
  modelData.background = null;
  modelData.format = {
    label: 'Sunset Vibes',
    background: 'linear-gradient(350deg, rgba(255,143,107,1) 0%, rgba(255,94,98,1) 20%, rgba(225,78,202,1) 40%, rgba(133,50,208,1) 60%, rgba(70,30,192,1) 80%, rgba(30,0,158,1) 100%)',
    timerFullBackground: '#ff5e62', 
    timerEmptyBackground: '#8732d0', 
    categoryText: '#ffffff', 
    segmentBackground: '#4634c8', 
    segmentColor: '#ffd2d3',
    boxColor: '#ffffff',
    boxBackground: '#1e1e1e',
    secondBoxColor: '#1e1e1e',
    secondBoxBackground: '#ffffff',
  };
  modelData.answerData = {
    text: '',
    highlight: [],
    reorder: [],
    hint: { show: false, amount: null, delay: false }
  };
  return modelData;
});


function addQuestion(round) {
  const model = JSON.parse(JSON.stringify(newQuestionModel.value));
  if (!store.CurrentGame.rounds[round]) store.CurrentGame.rounds[round] = [];
  store.CurrentGame.rounds[round].push(model);
  store.CurrentGame.activeQuestion = store.CurrentGame.rounds[round].length - 1;
  // Not going to save to Database upon creation from inside the Active Game engine
  // store.saveData({collection: 'questions', records: [model]}, round);
  
}
function removeQuestion(round, question) {
  console.log(store.CurrentGame.rounds[round]);
  store.CurrentGame.rounds[round].splice(question, 1);
  if (store.CurrentGame.rounds[round].length === 0) {
    addQuestion(round);
    store.CurrentGame.activeQuestion = 0;
  } else if (store.CurrentGame.rounds[round][question - 1])
    store.CurrentGame.activeQuestion = question - 1;
  else {
    store.CurrentGame.activeQuestion = 0;
  }
  deleteConfirmQuestion.value = null;
}
function moveQuestion(key, up) {
  const tempStorage = { ...store.CurrentGame.rounds[activeRound.value][key] };
  store.CurrentGame.rounds[activeRound.value][key] = store.CurrentGame.rounds[activeRound.value][up ? key - 1 : key + 1]
  store.CurrentGame.rounds[activeRound.value][up ? key - 1 : key + 1] = tempStorage;
  activeQuestion.value = up ? key - 1 : key + 1;
}




function addRound(copyPrevious) {
  let newKey = Object.keys(store.CurrentGame.rounds).length;
  if (!copyPrevious) {
    // Old Way
    // props.questions.push(blankRound());
    // question.value = 0;
    // New Way
    store.CurrentGame.rounds[newKey] = [];
  } else {
    let newKey = Object.keys(store.CurrentGame.rounds).length;
    store.CurrentGame.rounds[newKey] = JSON.parse(JSON.stringify(store.CurrentGame.rounds[activeRound.value]));
  }
  activeQuestion.value = 0;
  activeRound.value = newKey;
  // round.value = game.value.rounds.length - 1;
}

function removeRound(key) {
  delete store.CurrentGame.rounds[key];
  const keys = Object.keys(store.CurrentGame.rounds).sort();
  let newRounds = {};
  for (let i = 0; i < keys.length; i += 1) {
    newRounds[i] = store.CurrentGame.rounds[keys[i]];
  }
  store.CurrentGame.rounds = newRounds;
  if (Object.keys(store.CurrentGame.rounds).length === 0) {
    addRound();
  }
  const newRound = Number(key) - 1 || 0;
  store.CurrentGame.activeRound = newRound;
  const newQuestion = store.CurrentGame.rounds[newRound][store.CurrentGame.activeQuestion] ? store.CurrentGame.activeQuestion : 0;
  store.CurrentGame.activeQuestion = newQuestion;
}


const deleteConfirmQuestion = ref(null);

const interlude = computed({
  get() {
    return store.CurrentGame.interlude;
  },
  set(newVal) {
    store.CurrentGame.interlude = newVal;
  }
});

function updateInterludeImage() {
  // Update media link when filename is provided
  let fileName = interlude.value.image.entry;
  if (fileName.substring(0,4).toLowerCase() !== 'http') {
    const link = new URL(`../assets/${fileName}`, import.meta.url);
    interlude.value.image.link = link.href;
  } else {
    interlude.value.image.link = fileName;
  }
}



function changeActiveRound(key) {
  activeRound.value = key;
  const q = activeQuestion.value;
  if (store.CurrentGame.rounds[key]) {
    if (!store.CurrentGame.rounds[key][q]) {
      if (!store.CurrentGame.rounds[key][0]) {
        addQuestion(key);
      }
      activeQuestion.value = 0;
      // question.value = 0;
    }
  } else {
    activeRound.value = 0;
  }
}

const unloadWarning = ref(false);

function unloadGame() {
  unloadWarning.value = false;
  store.unloadGame();
}


const connectToRemote = computed({
  get() {
    return store.connectToRemote;
  },
  set(newVal) {
    store.connectToRemote = newVal;
  }
});

const connectToRemoteStatus = computed({
  get() {
    return store.connectToRemoteStatus;
  },
  set(newVal) {
    store.connectToRemoteStatus = newVal;
  }
});

// watch(questionOverride, async () => {
//   questions.value = JSON.parse(questionOverride.value);
// });


// watch(() => 'props.questions', () => {
//         if (questions.length === 0) {
//           addRound();
//         }
//         emit('update', props.questions);
//         // saveStorage('questions', props.questions);
//       },
//       { deep: true }
// );
/*
const modifierKeyEditor = reactive({
  alt: false,
  ctrl: false,
});

// Watch for keystrokes
  onMounted(() => {
    window.addEventListener('keyup', ev => {
      switch(ev.key) {
        case('Alt'): {
          modifierKeyEditor.alt = false;
          break;
        }
        case('Control'): {
          modifierKeyEditor.ctrl = false;
          break;
        }
      }
    });
    window.addEventListener('keydown', ev => {
      switch(ev.key) {
        case('Alt'): {
          modifierKeyEditor.alt = true;
          break;
        }
        case('Control'): {
          modifierKeyEditor.ctrl = true;
          break;
        }
     }
   })
 })*/
</script>

<template>
  <div
      class="manager">
    <div
        class="manager__header">
      <button
          @click="unloadWarning = !unloadWarning"
          class="ml-6 mr-6"
          style="background-color: red; color: white">
      Unload Game
    </button>
      Question Editor
      <button
          @click="saveQuestions">
        Management Console
      </button>
      <button
          @click="saveGame()">
        Save Game
      </button>
    <button
        @click="emit('close')"
        class="mr-0 ml-6">
      Close
    </button>
    <button
        @click="hideAnswersInManager = !hideAnswersInManager"
        :style="hideAnswersInManager ? 'background-color: red' : ''">
      👁️ Answers
      <span v-text="hideAnswersInManager ? 'Hidden' : 'Shown'" />
    </button>
    <button
        @click="connectToRemote = !connectToRemote"
        :style="connectToRemote ? 'background-color: green' : ''">
      🌐
      <span
          v-if="connectToRemote && webSocketStore.connectionStatus === 'Connected' && connectToRemoteStatus">
        Controller Link Established
      </span>
      <span
          v-else-if="connectToRemote && webSocketStore.connectionStatus === 'Connected' && !connectToRemoteStatus">
        Connected to Server. Waiting for Controller
      </span>
      <span
          v-else-if="connectToRemote && webSocketStore.connectionStatus === 'Connecting'">
        Connecting to Server
      </span>
      <span
          v-else-if="connectToRemote && webSocketStore.connectionStatus === 'Disconnected'">
        Disconnected from Server
      </span>
      <span
          v-else>
        Connect to Remote
      </span>
    </button>
    </div>
    <div
        v-if="unloadWarning"
        class="manager__unload-warning">
      Are you sure? You will lose any unsaved progress.
      <button
          @click="unloadWarning = false"
          class="mr-4">
        Cancel
      </button>
      <button
          @click="unloadGame()">
        Unload Game
      </button>
    </div>
    <div>
      <div
          class="manager__rounds">
        <div
            class="manager__rounds__sidebar">
          <div
              :class="activeRound === -1 ? 'highlight' : ''"
              @click="activeRound = -1">
            Interlude Screen
          </div>
          <div
              v-for="(round, index) in store.CurrentGame.rounds"
              @click="changeActiveRound(index)"
              :class="index === activeRound ? 'highlight' : ''"
              :key="index">
            <move-and-delete
                v-if="activeRound === index"
                :content="'Round'"
                @up="moveRound(index, true)"
                @down="moveRound(index, false)"
                @delete="removeRound(index)"
                style="display: inline-block"
            />
            <span>
              Round {{Number(index) + 1}}
            </span>
            <div
                class="manager__rounds__questions"
                v-if="activeRound === index">
              <div
                  v-if="activeRound || activeRound === 0"
                  v-for="(question, num) in store.CurrentGame.rounds[activeRound]"
                  :key="num"
                  class="manager__rounds__question"
                  :class="activeQuestion === num ? 'highlight' : ''">
                <move-and-delete
                    v-if="activeQuestion === num"
                    :content="'Question'"
                    @delete="removeQuestion(activeRound, num)"
                    @up="moveQuestion(Number(num), true)"
                    @down="moveQuestion(Number(num), false)"
                />
                <div
                    class="manager__input-container"
                    @click="activeQuestion = num">
                  <span
                      class="manager__input-container__text">
                    {{ Number(num) + 1 }})
                  </span>
                  <span
                      v-if="question"
                      v-text="question.category"
                  />
                </div>
              </div>
              <button
                  v-if="(activeRound || activeRound === 0) && store.CurrentGame.rounds[activeRound]"
                  @click="addQuestion(activeRound);"
                  style="margin-top: 25px;">
                  ➕ Question
              </button>
            </div>
          </div>
          <div
              class="manager__rounds__sidebar__controls">
            <button
                @click="addRound();">
              + Round
            </button>
            <button
                class="small-button"
                @click="addRound(true);">
              Duplicate Current Round
            </button>
          </div>
        </div>
        <!-- Interlude Screen Editor -->
        <div
            class="manager__rounds__questions"
            v-if="activeRound === -1">
          <div>
            <label
                for="useInterlude">
              Enable Interlude
            </label>
            <input
                id="useInterlude"
                v-model="interlude.useInterlude"
                type="checkbox"
            />
            <br />
            <label
                for="showInterludeImage">
              Show Interlude Between Rounds
            </label>
            <input
                id="showInterludeImage"
                v-model="interlude.image.show"
                type="checkbox"
            />
            <!-- <label
                for="interludeImageMode">
              Per-round Images
            </label>
            <input
                id="interludeImageMode"
                v-model="interlude.image.perRound"
                type="checkbox"
            /> -->
            <!-- <div
                v-if="interlude.image.perRound"
                v-for="(text, key) in Object.keys(store.CurrentGame.rounds)"
                :key="key">
              {{`Round ${Number(key) + 1}:`}}
              <input
                  v-model="interlude.image.rounds[key]"
                  :placeholder="`Text before Round ${(Number(key) + 1)}`"
              />
            </div> -->
            <div>
              <input
                  v-model="interlude.image.entry"
                  placeholder="Image for all Rounds"
                  @input="updateInterludeImage()"
                  :disabled="!interlude.image.show"
              />
            </div>
          </div>
          <!-- Single text / multi text mode switcher -->
          <div
              class="mt-5">
            <label
                for="interludeTextMode">
              Per-round text
            </label>
            <input
                id="interludeTextMode"
                v-model="interlude.text.perRound"
                type="checkbox"
            />
            <div
                v-if="interlude.text.perRound">
              <div
                  v-for="(text, index) in Object.keys(store.CurrentGame.rounds)"
                  :key="index">
                {{`Round ${Number(index) + 1}:`}}
                <input
                    v-model="interlude.text.rounds[key]"
                    :placeholder="`Text before Round ${(Number(index) + 1)}`"
                />
              </div>
            </div>
          <!-- {{interlude.text}} -->
            <div
                v-else>
              <input
                  id="interludeTextAllInput"
                  v-model="interlude.text.all"
                  placeholder="Text"
              />
            </div>
          </div>
        </div>
        <div
            v-if="workingQuestion">
          {{workingQuestion._id}}
          <segment-editor
              :workingQuestion="workingQuestion"
              collection="questions"
              :manager="false"
              :hideAnswers="hideAnswersInManager"
          />
        </div>
      </div>
    </div>
    <!-- <div
        v-if="generators.wordLeap">
      Linguistic Leaps
      <button
          @click="generateWords()">
        Generate Words
      </button>
      <button
          @click="rerollSeed()">
          ↻
      </button>
      <input
          v-model="generators.seed"
          placeholder="Generate words likely to follow..."
      />
      <input
          v-model="generators.promptInput"
          placeholder="Blank prompt"
      />
      <div
          v-for="(wordSet, index) in generators.bank"
          :key="index"
          class="ai__wordSet">
        <div
            v-for="(word, key) in wordSet"
            :key="key"
            class="ai__word"
            :class="generators.highlightedWords.indexOf(word) > -1 ? 'highlight' : ''"
            @click="generateWords(word)">
          <span
              v-text="word"
          />
        </div>
      </div>
    </div> -->
    <!-- <div
        v-if="generators.sizeMatters">
      Size Matters
      <button
          @click="generateSizeMatters()">
        Generate
      </button>
    </div> -->
  </div>
</template>

<style
    lang="scss" scoped>
@import '@/assets/base.css';




img {
  max-height: 66vh;
  margin: auto;
  border-radius: 10px;
  display: flex;
}
/* .option-image {
  /* width: 21vw !important;
  height: 21vh !important;
} */
video {
  max-width: 90vw;
  max-height: 85vh;
  border: black solid 1px;
  border-radius: 15px;
  margin: auto;
  display: flex;
}




.answer-fade-enter-active {
  transition: all 0.7s ease-out;
}
.answer-fade-leave-active {
  transition: all 0.5s ease-out;
}
.answer-fade-enter-from {
  transform: translateX(-50vw);
  opacity: 0;
}
.answer-fade-leave-to {
  transform: translateX(50vw);
  opacity: 0;
}



.slide-fade-enter-active {
  transition: all 0.6s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.4s cubic-bezier(1, 0.5, 0.8, 1);
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateX(20px);
  opacity: 0;
}

.slide-right-enter-active, .slide-center-enter-active, .slide-left-enter-active {
  transition: all 0.6s ease-out;
}

.slide-right-leave-active, .slide-center-leave-active, .slide-left-leave-active {
  transition: all 0.6s ease-out;
}

.slide-right-enter-from {
  transform: translateX(-50px);
  opacity: 0;
}
.slide-right-leave-to {
  transform: translateX(50px);
  opacity: 0;
}
.fade-enter-active {
  transition: all 0.6s ease-out;
}

.fade-leave-active {
  transition: all 0.6s ease-out;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Trivia Night */

/* #question-container {
  position: relative;
} */

.small-button {
  font-size: 0.8em;
  margin-right: 1em;
}

/* .trivia-container {
  height: 87vh;
  width: 100vw;
  /* position: absolute; */
  /* display: flex; */
  /* top: 0;
  right: 0; */
  /* background: inherit; */
  /* font-size: 6.5em;
  text-align: center;
  align-items: center; */
  /* top: 50%;
  text-align: center;
  height: 90vh;
  width: 100vw; */
  /* position: absolute;
  top: 0;
  right: 0;
  height: 90vh;
  width: 100vw; */
  /* font-size: 7vmin;
  padding-bottom: 10vh; */
  /* margin-top: auto;
  margin-bottom: auto; */
/* 
.trivia-container::before {
  content: '';
  display: inline-block;
  height: 100%; 
  vertical-align: middle;
  /* margin-right: -0.25em; Adjusts for spacing */

  /* For visualization 
  background: #808080; width: 5px;
  
} */

.question {
  display: inline-block;
  vertical-align: middle;
  width: 100vw;
  /* position: relative;
  top: 0;
  transform: translateY(10%);
  width: 100vw; */
  /* height: calc(87vh - 1px); */
}

#ticker {
  background-color: gold;
}


.trivia-multiple-choice {
  background-color: darken(azure, 30%);
  color: darkslategrey;
  border: solid gold 2px;
  padding: 15px;
  margin: 1.5vh 1.5vw;
  border-radius: 5px;
  font-size: 0.8em;
  &__correct {
    /* background-color: gold; */
    color: darkslategrey;
    transition: all ease 1.1s;
  }
  &__incorrect {
    background-color: darkslategrey;
    color: lighten(darkslategrey, 50%);
    transition: all ease 1.1s;
  }
  &__0 {
    background-color: #fcd303;
    color: darken(#fcd303, 31%);
  }
  &__1 {
    background-color: #03fc77;
    color: darken(#03fc77, 31%);
  }
  &__2 {
    background-color: #03e3fc;
    color: darken(#03e3fc, 31%);
  }
  &__3 {
    background-color: lighten(#fc0341, 25%);
    color: darken(#fc0341, 41%);
  }
  &__4 {
    background-color: #1ab7e7;
    color: darken(#21cbff, 41%);
  }
  &__5 {
    background-color: #fc69ff;
    color: darken(#fc69ff, 51%);
  }
}

/* .trivia-container {
  height: 100vh;
} */




.answer-container {
  background-color: black;
  height: 12vh;
  bottom: 0vh;
  display: grid;
  justify-content: center;
  align-content: center;
}


.bet-text {
  font-size: 0.5em;
  margin: 10px;
  padding: 20px;
}
strong {
  font-weight: 800;
  color: gold;
}

/* .info {
  color: lighten(gold, 15%);
  text-shadow: 2px 2px 2px rgba(0, 0, 0, 0.3);
} */



.manager__unload-warning {
  background-color: rgba(249, 90, 45, 1);
  color: white;
  border-radius: 10px;
  padding: 10px;
  margin: 10px;
  text-align: center;
  border: 5px solid darkslategrey;
  font-size: 2em;
}



.memory-square {
    width: 20vw;
    height: 15vh;
    margin: 2vmax auto;
    border-radius: 5px;
    border: 5px solid white;
  }
  .blue {
    background-color: blue;
  }
  .blue-inactive {
    background-color: rgba(blue, 0.3);
  }
  .red {
    background-color: red;
  }
  .red-inactive {
    background-color: rgba(red, 0.3);
  }
  .yellow {
    background-color: yellow;
  }
  .yellow-inactive {
    background-color: rgba(yellow, 0.3);
  }
  .white {
    background-color: white;
  }
  .white-inactive {
    background-color: rgba(white, 0.3);
  }


  .word-leap {
    margin-bottom: 1vmax;
    display: flex;
    border-radius: 10px;
    padding: 0px 3vmax;
    &__container {
      background:darken( cornflowerblue, 35%);
      margin: auto;
      padding: none;
      width: 45vw;
    }
    &:first-of-type {
      color: black;
      background:darken( cornflowerblue, 35%);
    }
    &:last-of-type {
      color: black;
      background:darken(cornflowerblue, 35%);
    }
    &__letter {
      border: 2px solid gold;
      border-radius: 10px;
      margin: 3px;
      color: black;
      background: gold;
      width: 65px;
      height: 60px;
      padding: none;
      transition: all 2.2s ease-out;
      font-size: 2.5rem;
    }
    &__blank {
      background: cornflowerblue;
      color: cornflowerblue;
    }
  }

  .scoreboard {
    position: fixed;
    bottom: 0vh;
    width: 100vw;
    background: silver;
    transition: all 1s ease;
    margin: auto;
  }
  .teams {
    &__name {
      font-size: 3em;
      width: 60vw;
    }
    &__score {
      margin-left: 1vw;
      margin-right: 2vw;
      font-size:4em;
      line-height: 0;
      border: 2px solid azure;
      background: rgb(75,97,84);
      background: radial-gradient(circle, rgba(75,97,84,1) 0%, rgba(119,120,64,1) 100%);
      color: white;
      padding: 0px 25px;
      &__high {
        color: gold;
        font-weight: 800;
      }
    }
    &__controls {
      display: inline-block;
      width: 15vw;
      &__toAdd {
        width: 5vw;
      }
    }
  }

  .ai {
    &__wordSet {
      display: block;
      margin: 1em;
    }
    &__word {
      display: inline-block; 
      margin: 0.5em;
      background: cornflowerblue;
      padding: 0.5em;
      color: khaki;
      border-radius: 5px;
      cursor: pointer;
    }
  }
  .highlight {
      background-color: rgba(gold, 0.7);
      color: darkslategrey;
  }
  .manager {
    cursor: default;
    position: absolute;
    text-align: center;
    overflow: scroll;
    z-index: 99;
    top: 0;
    width: 100vw;
    &__input-container {
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      background: darkslategrey;
      padding: 5px;
      border-radius: 5px;
      border: solid 2px white;
      &__text {
        margin: 0px 10px;
      }
    }
    &__header {
      background-color: darkslategrey;
      border-radius: 10px;
      font-size: 2em;
      margin-left: auto;
      margin-right: auto;
      padding: 0.5em 0px;
    }
    &__rounds {
      width: 33vw;
      text-align: center;
      margin: 25px 10vw;
      &__sidebar {
        background: rgba(darkslategrey, 0.8);
        font-size: 2em;
        padding: 1vmax;
        margin-right: 10px;
        margin-left: auto;
        border-radius: 5px;
      }
      &__questions {
        background-color: darkslategrey;
        padding: 10px;
        margin: auto;
        font-size: 0.5em;
        margin-left: 10px;
      }
      &__question {
        padding: 8px;
        cursor: pointer;
      }
    }
    /* &__question-container {
      width: 66vw;
      margin-top: 15px;
      background: darkslategrey;
      display: flex;
      justify-content: center;
    } */
    /* &__segment-container {
      padding: 5px;
      background: khaki;
      color: black;
      width: 48vw;
    } */
    &__segment {
      background: darken(khaki, 20%);
      margin: 10px;
      padding: 10px;
      border-radius: 5px;
      border: 2px solid darkslategrey;
      &__select {
        margin-right: 10vw;
      }
    }
    &__segment-input {
      min-width: 50vw;
      min-height: 10vh;
    }
    &__question-number {
      background-color: azure;
      color: black;
      padding: 5px;
      border-radius: 5px;
      margin: 10px;
    }
    &__preview {
      margin: 20px auto;
      background-color: rgba(black, 0.6);
      padding: 10px 20px;
      border-radius: 10px;
      font-size: 1.3em;
    }
  }

  .ck-content {
     color: black;
  }
/* End Trivia Night */
</style>
